import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import fileTypes from '../../../common/fileTypes';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';
import Confirm from '../Confirm/Confirm';
import { deleteQueueItem } from '../../actions/assetActions';
import { formatBytes } from '../../utils/formatUnits';
import { classNames } from '../../utils/v4/utils';

export default class AssetsUploads extends Component {
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.queuePoll(nextProps);
  }

  componentWillUnmount() {
    if (this.queueInterval) {
      clearInterval(this.queueInterval);
    }
  }

  queuePoll = props => {
    const { encodingQueue, getEncodingQueue } = props;

    clearInterval(this.queueInterval);

    if (!encodingQueue.length) {
      return;
    }

    this.queueInterval = setInterval(getEncodingQueue, 5000);
  };

  getEncodingQueueItem = id => {
    const { encodingQueue } = this.props;

    return encodingQueue.find(item => item.uploadId === id);
  };

  getCustomError = item => {
    const { validation } = item;
    let info = 'Upload failed';

    if (!validation) {
      return info;
    }

    const { code } = validation;
    /**
     * TODO: #ian
     * - Create a proper object to hold error constants
     */
    const isDuplicate = code === 'FILE_NAME_EXISTS';
    const isQueuedLocal = code === 'FILE_ALREADY_QUEUED_LOCAL';
    const isQueuedEncoding = code === 'FILE_ALREADY_QUEUED_ENCODING';
    const isInvalidFileType = code === 'FILE_TYPE_NOT_SUPPORTED';

    if (isDuplicate) {
      let caption;
      let smallCaption;
      let link;
      let folder = 'assets';
      const {
        data: { assets: duplicateAssets, name: lookupname },
      } = validation;

      if (!Array.isArray(duplicateAssets)) {
        return info;
      }

      // TODO: #ian UI implemented for duplicate fonts and PDF documents,
      // but functionality needs to be added to backend
      if (this.isFont(item)) {
        caption = 'View Font';
        folder = 'fonts';
      } else if (this.isPdf(item)) {
        caption = 'View First Image';
        duplicateAssets[0].assetId = `${duplicateAssets[0].assetId}-0`;
      } else {
        caption = 'View Asset';
      }

      if (duplicateAssets.length > 1) {
        link = `/content?search=${lookupname}&parent=%2F*`;
        smallCaption = `File already exists (${duplicateAssets.length})`;
      } else {
        link = `/${folder}/${duplicateAssets[0]}`;
        smallCaption = 'File already exists';
      }

      info = (
        <div>
          <Link className="text-sunrise" to={link}>
            {caption}
          </Link>
          <div className="upload-info">{smallCaption}</div>
        </div>
      );
    } else if (isQueuedEncoding) {
      info = (
        <div>
          <span className="text-danger">Upload failed</span>
          <div className="upload-info">File in encoding queue</div>
        </div>
      );
    } else if (isQueuedLocal) {
      info = (
        <div>
          <span className="text-danger">Upload failed</span>
          <div className="upload-info">File already in queue</div>
        </div>
      );
    } else if (isInvalidFileType) {
      info = (
        <div>
          <span className="text-danger">Upload failed</span>
          <div className="upload-info">Unsupported file type</div>
        </div>
      );
    }

    return info;
  };

  getStatusInfo = item => {
    const { id, progress, error, supported } = item;
    const queueItem = this.getEncodingQueueItem(id);
    let status = 'info';
    let info;

    if (!supported) {
      status = 'danger';
      info = 'Not supported file format';
    } else if (error) {
      status = 'danger';
      info = this.getCustomError(item);
    } else if (!progress) {
      info = 'Waiting for upload';
    } else if (progress < 100) {
      info = `${progress}%`;
    } else {
      status = 'success';
      info = 'Upload completed';
      const isVideo = this.isVideo(item);
      const isPdf = this.isPdf(item);

      if (isVideo || isPdf) {
        status = 'success';
        info = isVideo ? 'Upload and encode complete' : 'Upload and conversion complete';

        if (queueItem) {
          switch (queueItem.status) {
            case 'waiting':
              status = 'info';
              info = isVideo ? 'Waiting for encoding' : 'Waiting to convert PDF';
              break;
            case 'processing':
              status = 'info';
              info = isVideo ? 'Encoding file' : 'Extracting images';
              break;
            case 'error':
              status = 'danger';
              info = isVideo ? 'Encode failed' : 'Image conversion failed';
              break;
            default:
          }
        }
      }
    }

    return { status, info };
  };

  isComplete = item => this.getStatusInfo(item).status === 'success' && Boolean(item.asset);

  isFinished = item => !item.supported || item.error || this.isComplete(item);

  onClickClose = item => {
    const { type, xhr, validation } = item;
    const { status } = this.getStatusInfo(item);

    if (validation?.code) {
      return this.removeFile(item);
    }

    if (type.includes('video') && status !== 'success') {
      const confirm = this.refDelete;

      confirm.show(
        'Remove Video from Encode Queue',
        <div>
          <p>Do you want to remove this video from the encode queue?</p>
          <b>{item.name}</b>
        </div>,
      );
      confirm.onSuccess(() => {
        if (item.asset) {
          deleteQueueItem(item.asset.id);
        }

        this.removeFile(item);

        if (xhr) {
          xhr.abort();
        }
      });
    } else {
      this.removeFile(item);

      if (xhr) {
        xhr.abort();
      }
    }
  };

  removeFile = item => {
    const { uploadRemoveItem } = this.props;

    window.SignStix.uploadingFiles = window.SignStix.uploadingFiles.filter(
      file => file.id !== item.id,
    );
    uploadRemoveItem(item.id);
  };

  onClickCloseAllFinished = () => {
    const { uploading } = this.props;

    uploading.forEach(item => {
      if (this.isFinished(item)) {
        this.removeFile(item);
      }
    });
  };

  getProgressTotal = () => {
    const { uploading } = this.props;
    let loaded = 0;
    let size = 0;
    let errors = 0;
    let finished = 0;

    uploading.forEach(item => {
      size += item.size;

      if (item.error) {
        errors += 1;
      } else {
        loaded += item.loaded || 0;
      }

      if (this.isFinished(item)) {
        finished += 1;
      }
    });

    return { loaded, size, progress: Math.min(100, (loaded / size) * 100), errors, finished };
  };

  getProgressInfo = item => {
    const { status, info } = this.getStatusInfo(item);

    if (!item.progress) {
      return info;
    }

    if (this.isComplete(item)) {
      if (this.isFont(item)) {
        return <Link to={`/fonts/${item.asset.id}`}>View Font</Link>;
      }

      if (this.isPdf(item)) {
        return <Link to={`/assets/${item.asset.id}-0`}>View First Image</Link>;
      }

      if (item.asset.id) {
        return <Link to={`/assets/${item.asset.id}`}>View Asset</Link>;
      }
    }

    if (this.isFinished(item) && typeof info === 'object') {
      return info;
    }

    return (
      <ProgressBar
        className="upload-progress-bar"
        bsStyle={status}
        now={item.progress}
        label={info}
      />
    );
  };

  isPdf = item => item.type.includes('pdf');

  isFont = item => fileTypes.font.some(ext => item.name.toLowerCase().endsWith(ext));

  isVideo = item => fileTypes.video.some(ext => item.name.toLowerCase().endsWith(ext));

  getCloseButton = item => {
    const { progress } = item;
    const text = `${progress < 100 ? 'Abort' : 'Close'} Upload`;

    return (
      <ToolTipIcon toolText={text} onClick={() => this.onClickClose(item)}>
        remove_circle_outline
      </ToolTipIcon>
    );
  };

  getUploads = () => {
    const { uploading } = this.props;
    const rows = uploading.map((item, index) => {
      const { type, name, parent, size, loaded, supported } = item;
      const { status } = this.getStatusInfo(item);
      const error = status === 'danger' ? 'error' : '';
      const complete = this.isComplete(item) ? 'complete' : '';

      return (
        <tr
          className={
            ('align-middle', complete ? 'bg-green-50/50' : '', error ? 'bg-red-50/50' : '')
          }
          key={index}
        >
          <td className="py-4 pl-2">
            <div className="font-medium text-dark-night poppins-bold">{name}</div>
            {supported && !this.isFont(item) && (
              <div className="mt-1 text-xs text-gray-500">Folder: {parent}</div>
            )}
          </td>
          <td className="py-4">
            <div className="text-gray-700 poppins-regular">
              {' '}
              {formatBytes(Math.min(loaded, size))} / {formatBytes(size)}
            </div>
            <div className="mt-1 text-xs text-gray-500 poppins-regular">{type}</div>
          </td>
          <td className="upload-progress h-[54px]">
            <div className="h-full flex items-center poppins-regular">
              {this.getProgressInfo(item)}
            </div>
          </td>
          <td className="upload-close text-right h-[56px] items-center flex justify-end poppins-regular">
            {this.getCloseButton(item)}
          </td>
        </tr>
      );
    });

    return (
      <table data-test="uploads-table" className="table">
        <tbody>{rows}</tbody>
      </table>
    );
  };

  render() {
    const { title = 'Uploading Assets', uploading } = this.props;
    const unsupported = uploading.filter(item => !item.supported).length;

    if (!uploading || !uploading.length) {
      return null;
    }

    const { progress, loaded, size, errors, finished } = this.getProgressTotal();
    const done = uploading.length === finished;
    const uploads = this.getUploads();
    let status = <span className="loading">{title}</span>;
    let color = 'info';

    if (progress === 100) {
      color = 'success';
    }

    if (errors || unsupported) {
      color = 'danger';
    }

    if (done) {
      if (errors || unsupported) {
        status = (
          <span className="rounded-full bg-red-100 px-3 py-1 font-semibold text-red-600">
            Finished with Errors{' '}
          </span>
        );
      } else {
        status = (
          <span className="rounded-full bg-green-100 px-3 py-1 font-semibold text-green-600">
            FINISHED
          </span>
        );
      }
    }

    return (
      <div
        data-test="asset-uploads"
        className="mt-8 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl"
      >
        <div className="relative border-b border-gray-100 px-8 py-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {status}
              <span className="text-gray-500">{progress.toFixed('2')}%</span>
              <span className="text-gray-500">
                {uploading.filter(item => !item.error && item.loaded >= item.size).length} /
                {uploading.length} Files
              </span>
              <span className="text-gray-500">
                {' '}
                {formatBytes(Math.min(loaded, size))} / {formatBytes(size)}
              </span>
              {Boolean(errors) && <span className="font-medium text-red-500">{errors} failed</span>}
              {Boolean(unsupported) && (
                <span className="font-medium text-red-500">{unsupported} not supported</span>
              )}
            </div>
            <ToolTipIcon
              onClick={this.onClickCloseAllFinished}
              toolText="Clear Finished Uploads"
              className="material-icons text-xl text-dark-night transition hover:text-red-500"
            >
              remove_circle_outline
            </ToolTipIcon>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={classNames(
                'h-full transition-all duration-300 poppins-regular',
                color === 'success' && 'bg-primary',
                color === 'danger' && 'bg-red-500',
                color === 'info' && 'bg-blue-500',
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className="py-4 px-[24px] ">{uploads}</div>
        <Confirm
          ref={ref => {
            this.refDelete = ref;
          }}
        />
      </div>
    );
  }
}

AssetsUploads.propTypes = {
  title: PropTypes.string,
  uploading: PropTypes.array,
  encodingQueue: PropTypes.array,
  uploadRemoveItem: PropTypes.func.isRequired,
  getEncodingQueue: PropTypes.func.isRequired,
};
